<template>
  <div
    v-observe-visibility="visibilityChanged"
    class="product row m0 pb13 h-100 relative"
    :class="[artist ? 'artist-item' : 'category-item']"
  >
    <transition name="fade">
      <best-seller-popup-modal
        v-if="showBestSellerPopup === product.sku"
      />
    </transition>
    <transition name="fade">
      <tickets-left-popup-modal
        v-if="showTicketsLeftPopup === product.sku"
      />
    </transition>
    <a
      class="product-link"
      :href="productLink"
      draggable="false"
      :aria-label="cardLabel"
      @click="onCardClick"
    >
      <div class="date-wrapper pt10 visible-mobile hidden-tablet">
        <div
          class="date flex flex-column align-items-center c-bg-primary c-white pt17 pb10"
          v-html="getDate"
        />
      </div>
      <div
        class="item-info w-100 c-bg-white flex justify-space-between p15 pl23"
        :class="{ 'best-seller' : isBestSelling === true}"
      >
        <div class="col-xs-6 col-md-12 py8 item-info-content">
          <h3 class="w-100 m0 regular pb5 flex flex-row">
            <div class="date-wrapper pt10 hidden-mobile">
              <div
                class="date flex flex-column align-items-center c-bg-primary c-white pt17 pb10"
                v-html="getDate"
              />
            </div>
            <span class="product-header">
              <span
                style="-webkit-box-orient: vertical;"
                class="clamp-lines clamp-2"
                :class="{ 'category-title': !isProductPage }"
                lang="sv"
              >
                {{ productHeader }}
              </span>
            </span>
          </h3>
          <div class="attributes c-grayscale-dark fs14 lh17 flex flex-column">
            <div
              v-if="artist"
              class="attribute-wrapper flex flex-wrap"
            >
              <div
                v-if="locationAttribute"
                class="location-attr w-100"
              >
                <span class="hidden-mobile m0 row">{{ $t('Location') }}</span>
                <span
                  class="data clamp-lines clamp-1"
                  style="-webkit-box-orient: vertical;"
                >{{ locationAttribute.label }}</span>
              </div>
              <div class="event-attr w-100">
                <span class="m0 row hidden-mobile">{{ $t('Event') }}</span>
                <span class="data">
                  <span>{{ product.name }}</span>
                </span>
              </div>
            </div>
            <div v-else>
              <div
                v-if="cityAttribute"
                class="city-attr"
              >
                <span class="hidden-mobile m0 row">{{ $t('City') }}</span>
                <span
                  class="data clamp-lines clamp-1"
                  style="-webkit-box-orient: vertical;"
                >{{ cityAttribute.label }}</span>
              </div>
              <div
                v-if="locationAttribute"
                class="location-attr"
              >
                <span class="hidden-mobile m0 row">{{ $t('Location') }}</span>
                <span
                  class="data clamp-lines clamp-1"
                  style="-webkit-box-orient: vertical;"
                >{{ locationAttribute.label }}</span>
              </div>
            </div>
            <div class="date-attr align-items-center fs12 lh15 flex flex-wrap pt2">
              <span class="hidden-mobile w-100">{{ $t('Time') }}</span>
              <i class="icon icon-clock visible-mobile ml3 mr8" />
              <span class="data">{{ getTime }}</span>
            </div>
          </div>
        </div>
        <div class="button-wrapper col-xs col-md-12 p0 align-right">
          <span
            v-if="productQty > 0"
            class="button button-primary px10 py8 fs14 lh17 bold align-items-center event-button"
          >
            {{ $t('Go to event') }}
          </span>
          <span
            v-else
            class="button button-primary disabled px10 py8 fs14 lh17 bold align-items-center"
          >
            <span v-if="isStockInfoLoading">
              {{ $t('Loading...') }}
            </span>
            <span v-else>
              {{ $t('Sold out') }}
            </span>
          </span>
        </div>
      </div>
    </a>
    <transition name="fade">
      <best-seller
        v-if="artist && isBestSelling"
        :product-sku="product.sku"
        :artist="artist"
      />
    </transition>
    <transition name="fade">
      <tickets-left
        v-if="artist && isTicketsLeft && !isBestSelling && !isStockInfoLoading && productQty > 0"
        :max-quantity="productQty"
        :product-sku="product.sku"
      />
    </transition>
    <script
      v-if="isProductPage && inStock"
      type="application/ld+json"
      v-html="getJsonLd"
    />
  </div>
</template>

<script>
import rootStore from '@vue-storefront/core/store'
import { ProductTile } from '@vue-storefront/core/modules/catalog/components/ProductTile.ts'
import config from 'config'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { mapGetters, mapState } from 'vuex'
import { timeToDayJS } from 'theme/helpers/timeHelper'
import BestSeller from 'theme/components/theme/blocks/Category/BestSeller'
import { timeout } from 'theme/helpers'
import TicketsLeft from 'theme/components/theme/blocks/Category/TicketsLeft'
import { isServer } from '@vue-storefront/core/helpers'
import TicketsLeftPopupModal from 'theme/components/theme/blocks/Category/TicketsLeftPopupModal'
import BestSellerPopupModal from 'theme/components/theme/blocks/Category/BestSellerPopupModal'
import ScrollLock from 'theme/mixins/scrollLock'

export default {
  name: 'ProductTile',
  components: {
    BestSeller,
    TicketsLeft,
    TicketsLeftPopupModal,
    BestSellerPopupModal
  },
  mixins: [ProductTile, ScrollLock],
  props: {
    labelsActive: {
      type: Boolean,
      default: true
    },
    onlyImage: {
      type: Boolean,
      default: false
    },
    artist: {
      default: false,
      type: null
    }
  },
  data () {
    return {
      isStockInfoLoading: true
    }
  },
  computed: {
    ...mapState({
      showTicketsLeftPopup: state => state.ui.showTicketsLeftPopup,
      showBestSellerPopup: state => state.ui.showBestSellerPopup
    }),
    ...mapGetters({
      getAttributesByCode: 'attribute/attributeListByCode',
      getScrollPosition: 'scroll-handler/getScrollPosition',
      getCategories: 'category-next/getMenuCategories'
    }),
    jsonLdOffers () {
      let offers = ''
      if (this.product.configurable_children) {
        this.product.configurable_children.forEach(item => {
          let offer = `${offers ? ',' : ''}{
          "@type": "Offer",
          "name": "${this.artist ? this.artist.name : ''}",
          "price": "${item.original_price_incl_tax}",
          "priceCurrency": "SEK",
          "url": "${this.product.url_path}",
          "availability": "https://schema.org/${item.stock?.is_in_stock ? 'InStock' : 'SoldOut'}"
        }`

          offers = offers + offer
        })
      }
      return offers
    },
    getJsonLd () {
      return `{
        "@context": "https://schema.org",
        "@type": "Event",
        "name": "${this.product ? this.product.name : ''}",
        "image": {
          "@type": "ImageObject",
          "url": "${this.product.image === 'no_selection' ? '/no_selection' : this.product.image}"
        },
        "startDate": "${timeToDayJS(this.product.date).format('YYYY-MM-DDTHH:mm')}",
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "location": {
          "@type": "Place",
          "name": "${this.locationAttribute ? this.locationAttribute.label : ''}",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "${this.cityAttribute ? this.cityAttribute.label : ''}",
            "addressCountry": "SE"
          }
        },
        "performer": {
          "@type": "Person",
          "name": "${this.artist ? this.artist.name : ''}"
        },
        "offers": [${this.jsonLdOffers}]
      }`
    },
    inStock () {
      const isInStock = this.product.stock?.is_in_stock

      return isInStock != null ? isInStock : 0
    },
    isProductPage () {
      return !!(this.artist && this.cityAttribute)
    },
    storeView () {
      return currentStoreView()
    },
    locationAttribute () {
      if (this.getAttributesByCode.location) {
        return this.getAttributesByCode.location.options.find(option => Number(option.value) === this.product.location ? option : '')
      }
      return false
    },
    cityAttribute () {
      if (this.getAttributesByCode.city) {
        return this.getAttributesByCode.city.options.find(option => Number(option.value) === this.product.city ? option : '')
      }
      return false
    },
    getTime () {
      return timeToDayJS(this.product.date).format('HH:mm')
    },
    getDate () {
      if (this.product.date) {
        const day = timeToDayJS(this.product.date).format('DD')
        const month = timeToDayJS(this.product.date).format('MMM')
        const shortDayName = timeToDayJS(this.product.date).format('ddd')

        return `<span class="fs20 bold lh27">${day}</span><span class="uppercase pt2">${month}</span><span class="fs12 lh15 pb2 visible-mobile hidden-tablet capitalize">${shortDayName}</span>`
      }
      return ''
    },
    artistCategory () {
      const cats = this.product.category
      if (!Array.isArray(cats) || !cats.length) return null

      const productCategoryIds = cats
        .map(c => Number(c?.category_id))
        .filter(id => !Number.isNaN(id) && id > 0)

      const menuCategories = this.getCategories
      if (!Array.isArray(menuCategories) || !menuCategories.length || !productCategoryIds.length) {
        return null
      }

      return menuCategories.find(c =>
        c.level === 5 &&
        c.is_artist === true &&
        productCategoryIds.includes(Number(c.id))
      ) || null
    },
    firstUsableCategory () {
      return this.artistCategory ||
        (this.product.category || []).find(c => c && c.url)
    },
    productCategory () {
      const category = this.firstUsableCategory
      return category ? category.name : this.product.name
    },
    isBestSelling () {
      return !!(this.product.best_selling_event || this.product.best_selling_event_manual)
    },
    isTicketsLeft () {
      return !!(this.product.tickets_left)
    },
    productQty () {
      let productQty = 0
      if (this.product.configurable_children) {
        this.product.configurable_children.forEach((product) => {
          if (product.stock.qty >= 0) {
            productQty = productQty + product.stock.qty
          }
        })
      }
      this.stopStockLoading()
      return productQty
    },
    productHeader () {
      if (this.artist && this.cityAttribute) {
        return this.cityAttribute.label
      }
      return this.productCategory
    },
    cardLabel () {
      return [
        this.productHeader,
        this.artist ? this.product.name : '',
        this.product.date ? timeToDayJS(this.product.date).format('D MMMM') : '',
        !this.isProductPage && this.cityAttribute ? this.cityAttribute.label : '',
        this.locationAttribute ? this.locationAttribute.label : '',
        this.product.date ? this.getTime : '',
        this.productQty > 0 ? this.$t('Go to event') : this.$t('Sold out')
      ].filter(Boolean).join(', ')
    }
  },
  beforeMount () {
    this.$bus.$on('product-after-priceupdate', this.onProductPriceUpdate)
  },
  beforeDestroy () {
    this.$bus.$off('product-after-priceupdate', this.onProductPriceUpdate)
  },
  methods: {
    stopStockLoading () {
      this.isStockInfoLoading = false
    },
    onCloseModal () {
      this.$bus.$emit('modal-hide', 'modal-addToCart')
      this.clearAllBodyScrollLocks()
    },
    onProductPriceUpdate (product) {
      if (product.sku === this.product.sku) {
        Object.assign(this.product, product)
      }
    },
    visibilityChanged (isVisible, entry) {
      if (
        isVisible &&
        config.products.configurableChildrenStockPrefetchDynamic &&
        config.products.filterUnavailableVariants &&
        this.product.type_id === 'configurable' &&
        this.product.configurable_children &&
        this.product.configurable_children.length > 0
      ) {
        const skus = [this.product.sku]
        for (const confChild of this.product.configurable_children) {
          const cachedItem = rootStore.state.stock.cache[confChild.id]
          if (typeof cachedItem === 'undefined' || cachedItem === null) {
            skus.push(confChild.sku)
          }
        }
        if (skus.length > 0) {
          rootStore.dispatch('stock/list', { skus: skus }) // store it in the cache
        }
      }
    },
    onCardClick (event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      event.preventDefault()

      const selection = window.getSelection()
      if (selection && !selection.isCollapsed && this.$el.contains(selection.anchorNode)) return

      this.showProductRouteLoader()
    },
    async showProductRouteLoader () {
      if (this.isBestSelling) {
        if (this.productRouteLoader) this.productRouteLoader.cancel()
        this.$store.commit('ui/setProductRouteLoader', true)
        this.productRouteLoader = timeout(6000)
        await this.productRouteLoader.promise
        this.$store.commit('ui/setProductRouteLoader', false)
      }
      await this.$router.push(this.productLink)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/variables/grid';
@import '~theme/css/helpers/functions/z-index-selectors';
@import '~theme/css/helpers/mixins/card-hover';

.product-link {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  color: inherit;
  font-size: inherit;
  line-height: inherit;
  user-select: text;
  -webkit-user-drag: none;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid color(primary);
    outline-offset: 2px;
    border-radius: 10px;
  }
}

@include mobile {
  .category-title {
    font-size: 15px;
    line-height: 18px;
  }

  .product-link:hover .product-header {
    color: color(primary-hover);
  }

  .product-link:active .product-header {
    color: color(primary-pressed);
  }

  .date-wrapper {
    width: 45px;

    .date {
      border-radius: 10px 0 0 10px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
    }
  }

  .item-info {
    border-radius: 10px;
    position: relative;
    width: calc(100% - 45px);

    &-content {
      max-width: unset;
      flex: 1;
    }

    &::before {
      border-radius: 10px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: -1;
    }

    &.best-seller::before {
      border-radius: 10px 10px 0 0;
    }

    .icon-clock {
      height: 18px;
      width: 12px;
    }

    .button-wrapper {
      margin-bottom: 43px;
      max-width: fit-content;
    }
  }

  .event-button {
    max-width: calc(100% - 50px);
    min-width: fit-content;
  }

  h3 {
    font-size: 16px;
    line-height: 19px;
  }
}

.button {
  border-radius: 15px;
}

.clamp-lines {
  overflow: hidden;
  /* stylelint-disable-next-line value-no-vendor-prefix -- line clamping needs the prefixed box model */
  display: -webkit-box;
  hyphens: auto;
}

.clamp-2 {
  -webkit-line-clamp: 2;
}

.clamp-1 {
  -webkit-line-clamp: 1;
}

@include mediaQuery(sm) {
  .product {
    padding-bottom: 0;

    &.category-item {
      .button-wrapper {
        padding-top: 36px;
      }
    }
  }

  .item-info {
    flex-wrap: wrap;
    padding: 12px;
    border-radius: 10px;
    box-shadow: 0 2px 4px 0 rgba(color(black), 0.25);

    @include card-hover($shadow: 0 10px 22px 0 rgba(color(black), 0.22));

    h3 {
      font-size: 24px;
      line-height: 29px;
      font-weight: bold;
      align-items: center;
      margin-bottom: 12px;
    }

    .date-wrapper {
      width: 60px;
      padding-top: 0;
      margin-right: 26px;

      .date {
        border-radius: 9px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
        height: 67px;
        padding-top: 10px;
        width: 60px;

        /deep/ .fs20.lh27 {
          font-size: 24px;
        }

        /deep/ .uppercase {
          font-weight: 400;
          font-size: 18px;
          line-height: 17px;
          padding-top: 0;
        }
      }
    }
  }

  .attributes,
  .attributes .fs12.lh15 {
    font-size: 16px;
    line-height: 19px;
  }

  .attributes {
    .data {
      font-size: 18px;
      line-height: 22px;
      font-weight: bold;
      color: color(black);
    }

    .location-attr {
      order: 2;
      padding-top: 7px;
    }

    .event-attr {
      order: 1;
    }

    .date-attr {
      padding-top: 8px;
    }
  }

  .button-wrapper {
    display: flex;
    align-items: flex-end;
    padding-top: 14px;
  }

  .button {
    width: 100%;
    font-size: 16px;
    line-height: 19px;
    border-radius: 10px;
  }

  .product-link:hover .product-header {
    color: color(primary-hover);
  }

  .product-link:active .product-header {
    color: color(primary-pressed);
  }
}
</style>
